import { css } from '@linaria/atomic';
import { i18next, useTranslation } from '@frontend/i18n';
import { titleCase } from '@frontend/string';
import { theme } from '@frontend/theme';
import { Button, Chip, Modal, ModalControlModalProps, Text, useModalControl } from '@frontend/design-system';
import { DemoOrganizationFormValues } from './create-demo-organization-modal';
import { DeleteDemoOrganizationModal } from './delete-demo-organization-modal';
import { DemoIntegrationOption, getDemoIntegrationLabel } from './demo-integrations';

interface Props extends ModalControlModalProps {
  organization: DemoOrganizationFormValues & {
    name?: string;
    daysLeft?: number;
  };
  onRemove?: () => void;
  integrationOptions?: DemoIntegrationOption[];
}

type DemoBundle = 'BASE' | 'COORDINATE' | 'ATTRACT';

const BUNDLE_LABELS: Record<DemoBundle, string> = {
  BASE: i18next.t('Base', { ns: 'demo-location-ui' }),
  COORDINATE: i18next.t('Coordinate', { ns: 'demo-location-ui' }),
  ATTRACT: i18next.t('Attract', { ns: 'demo-location-ui' }),
};

const VERTICAL_LABELS: Record<string, string> = {
  dental: i18next.t('Dental', { ns: 'demo-location-ui' }),
  optometry: i18next.t('Optometry', { ns: 'demo-location-ui' }),
  veterinary: i18next.t('Veterinary', { ns: 'demo-location-ui' }),
  medical: i18next.t('Medical', { ns: 'demo-location-ui' }),
  specialty: i18next.t('Specialty', { ns: 'demo-location-ui' }),
};

const formatAddonLabel = (addon: string) => titleCase(addon.replace(/_/g, ' '));

export const ViewDemoSetupModal = ({ show, onClose, organization, onRemove, integrationOptions = [] }: Props) => {
  const { t } = useTranslation('demo-location-ui');
  const deleteModalControl = useModalControl();
  const organizationName = organization.name || organization.organizationName;
  const daysLeftText =
    organization.daysLeft !== undefined ? t('{{count}} days left!', { count: organization.daysLeft }) : '';
  const bundleLabel = organization.weaveBundle
    .map((bundle) => BUNDLE_LABELS[bundle as DemoBundle] ?? bundle)
    .join(', ');

  const handleRemoveClick = () => {
    deleteModalControl.openModal();
  };

  const handleConfirmDelete = () => {
    onRemove?.();
    deleteModalControl.closeModal();
    onClose();
  };

  return (
    <>
      <Modal show={show} onClose={onClose} minWidth={450} maxWidth={600}>
        <Modal.Header onClose={onClose}>{organizationName}</Modal.Header>
        <Modal.Body className={bodyStyles}>
          <div className={headerInfoStyles}>
            <Text>{t('This is a temporary demo organization.')}</Text>
            {daysLeftText && <Chip variant='eggplant'>{daysLeftText}</Chip>}
          </div>

          <div className={detailsGridStyles}>
            <div className={detailsColumnStyles}>
              <DetailRow label={t('Weave Bundle')} value={bundleLabel} />
              <DetailRow
                label={t('Integration')}
                value={t(getDemoIntegrationLabel(organization.integration, integrationOptions))}
              />
              {organization.vertical && (
                <DetailRow
                  label={t('Vertical')}
                  value={VERTICAL_LABELS[organization.vertical] || organization.vertical}
                />
              )}
            </div>
            <div className={detailsColumnStyles}>
              <DetailRow
                label={t('Addons')}
                value={
                  organization.addons.length > 0 ? organization.addons.map(formatAddonLabel).join(', ') : t('No addons')
                }
              />
              <DetailRow
                label={t('Phones')}
                value={organization.includePhones ? t('Include phones') : t('No phones')}
              />
              {/* <DetailRow
              label={t('Multi-location')}
              value={organization.locationCount === 'single' ? t('Single location') : t('Multi-location')}
            /> */}
            </div>
          </div>

          {onRemove && (
            <div className={removeButtonContainerStyles}>
              <Button variant='primary' destructive onClick={handleRemoveClick}>
                {t('Remove organization')}
              </Button>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <DeleteDemoOrganizationModal
        {...deleteModalControl.modalProps}
        organizationName={organizationName}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className={detailRowStyles}>
    <Text as='div' weight='bold'>
      {label}
    </Text>
    <Text>{value}</Text>
  </div>
);

const bodyStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(3)};
  padding-top: ${theme.spacing(3)};
  padding-right: ${theme.spacing(3)};
  padding-bottom: ${theme.spacing(3)};
  padding-left: ${theme.spacing(3)};
`;

const headerInfoStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

const detailsGridStyles = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing(3)};
`;

const detailsColumnStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`;

const detailRowStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(0.5)};
`;

const removeButtonContainerStyles = css`
  display: flex;
  justify-content: flex-end;
`;
